import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  OnDestroy,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { getCustomerServicePhone, getCustomerServiceTel } from '@ld.consumer-portal/constants';
import { ShareScreenService } from '@ld.consumer-portal/data-access';
import { heroiconScreenshare, supportIcon } from '@ld.consumer-portal/data-uri';
import type { FaqCategory, FaqQuestionAnswer } from '@ld.consumer-portal/models';
import { COMPOSE_MESSAGE_LAUNCHER, LoanSelectionService, PlatformService, ServisBotService } from '@ld.consumer-portal/utils';
import { merge } from 'rxjs';
import { map } from 'rxjs/operators';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';
@Component({
  selector: 'ld-help-faq',
  imports: [
    SvgIconComponent,
    FormsModule,
    RouterLink,
    MatExpansionModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    TranslocoDirective,
  ],
  templateUrl: './help-faq.component.html',
  styleUrl: './help-faq.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HelpFaqComponent implements AfterViewInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly transloco = inject(TranslocoService);
  private readonly platformService = inject(PlatformService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly servisBotService = inject(ServisBotService);
  private readonly loanSelectionService = inject(LoanSelectionService);
  private readonly router = inject(Router);
  private readonly elementRef = inject(ElementRef);
  private readonly composeMessageLauncher = inject(COMPOSE_MESSAGE_LAUNCHER, { optional: true });
  private readonly shareScreenService = inject(ShareScreenService);

  private readonly faqClickHandler = (event: Event): void => {
    const target = event.target as HTMLElement;
    if (target.closest('.secure-message-link')) {
      event.preventDefault();
      this.openMessageCenter();
      return;
    }

    const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
    if (anchor && anchor.protocol !== 'javascript:' && anchor.hostname !== window.location.hostname) {
      event.preventDefault();
      window.open(anchor.href, '_blank', 'noopener,noreferrer');
    }
  };

  protected readonly expandedSlug = signal<string | null>(null);

  protected readonly isNativeMobile = this.platformService.isNativeMobile;

  private readonly accountId = this.route.snapshot.paramMap.get('accountId');

  protected readonly contactUsLink = computed(() => {
    return this.accountId ? `/help/${this.accountId}/contact-us` : '/dashboard';
  });

  private readonly activeLang = toSignal(
    merge(this.transloco.langChanges$, this.transloco.events$.pipe(map(() => this.transloco.getActiveLang()))),
    { initialValue: this.transloco.getActiveLang() },
  );

  readonly isMobile = input(false);

  protected readonly searchText = signal('');
  protected readonly customerServicePhone = computed(() =>
    getCustomerServicePhone(this.loanSelectionService.customerServiceLoanCategory()),
  );
  protected readonly customerServiceTel = computed(() => getCustomerServiceTel(this.loanSelectionService.customerServiceLoanCategory()));
  protected readonly supportIcon = supportIcon;
  protected readonly screenshareIcon = heroiconScreenshare;
  protected readonly isShareScreenLoading = this.shareScreenService.isStarting;

  protected readonly isLoading = signal(false);
  protected readonly error = signal<string | null>(null);

  protected readonly faqCategories = computed<FaqCategory[]>(() => {
    this.activeLang();
    const t = (key: string, params?: Record<string, unknown>) => this.transloco.translate(key, params);
    const phoneParams = { customerServicePhone: this.customerServicePhone() };
    return [
      {
        faqType: t('helpFaqCategoriesGeneralQuestions'),
        faqQuestionAnswers: [
          {
            heading: t('helpFaqQuestionsWhyLoanNumberChangeHeading'),
            detailPart: t('helpFaqQuestionsWhyLoanNumberChangeAnswer'),
          },
          {
            heading: t('helpFaqQuestionsWillLoanBeTransferredHeading'),
            detailPart: t('helpFaqQuestionsWillLoanBeTransferredAnswer'),
          },
          {
            heading: t('helpFaqQuestionsWhyMortgageTransferredHeading'),
            detailPart: t('helpFaqQuestionsWhyMortgageTransferredAnswer'),
            detailPart2: t('helpFaqQuestionsWhyMortgageTransferredAnswer2'),
          },
          {
            heading: t('helpFaqQuestionsSubmitNoticeOfErrorHeading'),
            detailPart: t('helpFaqQuestionsSubmitNoticeOfErrorAnswer'),
          },
          {
            heading: t('helpFaqQuestionsSubmitQuestionElectronicallyHeading'),
            detailPart: t('helpFaqQuestionsSubmitQuestionElectronicallyAnswer'),
            displayOn: 'SMC',
          },
          {
            heading: t('helpFaqQuestionsWhatIsMfaHeading'),
            detailPart: t('helpFaqQuestionsWhatIsMfaAnswer'),
            detailPart2: t('helpFaqQuestionsWhatIsMfaAnswer2'),
          },
        ],
      },
      {
        faqType: t('commonPayments'),
        faqQuestionAnswers: [
          {
            heading: t('helpFaqQuestionsPaymentOptionsHeading'),
            detailPart: t('helpFaqQuestionsPaymentOptionsAnswer', phoneParams),
          },
          {
            heading: t('helpFaqQuestionsPayWithCreditCardHeading'),
            detailPart: t('helpFaqQuestionsPayWithCreditCardAnswer'),
          },
          {
            heading: t('helpFaqQuestionsRequestPayoffStatementHeading'),
            detailPart: t('helpFaqQuestionsRequestPayoffStatementAnswer', phoneParams),
          },
          {
            heading: t('helpFaqQuestionsPaperlessOptionHeading'),
            detailPart: t('helpFaqQuestionsPaperlessOptionAnswer'),
            displayOn: 'Paperless',
          },
          {
            heading: t('helpFaqQuestionsPaymentAssistanceHeading'),
            detailPart: t('helpFaqQuestionsPaymentAssistanceAnswer'),
            detailPart2: t('helpFaqQuestionsPaymentAssistanceAnswer2', phoneParams),
          },
        ],
      },
      {
        faqType: t('commonHeloc'),
        faqQuestionAnswers: [
          {
            heading: t('helpFaqQuestionsRequestDrawHeading'),
            detailPart: t('helpFaqQuestionsRequestDrawAnswer'),
          },
          {
            heading: t('helpFaqQuestionsWhenPaymentChangeHeading'),
            detailPart: t('helpFaqQuestionsWhenPaymentChangeAnswer'),
          },
          {
            heading: t('helpFaqQuestionsChangeDueDateHeading'),
            detailPart: t('helpFaqQuestionsChangeDueDateAnswer'),
          },
          {
            heading: t('helpFaqQuestionsAvailableCreditHeading'),
            detailPart: t('helpFaqQuestionsAvailableCreditAnswer'),
          },
          {
            heading: t('helpFaqQuestionsChangeBankAccountHeading'),
            detailPart: t('helpFaqQuestionsChangeBankAccountAnswer'),
          },
          {
            heading: t('helpFaqQuestionsChecksOrCreditCardsHeading'),
            detailPart: t('helpFaqQuestionsChecksOrCreditCardsAnswer'),
          },
          {
            heading: t('helpFaqQuestionsMoreInterestHeading'),
            detailPart: t('helpFaqQuestionsMoreInterestAnswer'),
          },
          {
            heading: t('helpFaqQuestionsCreditLineFrozenHeading'),
            detailPart: t('helpFaqQuestionsCreditLineFrozenAnswer'),
          },
          {
            heading: t('helpFaqQuestionsUnfreezeCreditLineHeading'),
            detailPart: t('helpFaqQuestionsUnfreezeCreditLineAnswer'),
          },
          {
            heading: t('helpFaqQuestionsPayDownCreditLineHeading'),
            detailPart: t('helpFaqQuestionsPayDownCreditLineAnswer', phoneParams),
          },
          {
            heading: t('helpFaqQuestionsIncreaseCreditLineHeading'),
            detailPart: t('helpFaqQuestionsIncreaseCreditLineAnswer'),
          },
          {
            heading: t('helpFaqQuestionsWhereMakePaymentHeading'),
            detailPart: t('helpFaqQuestionsWhereMakePaymentAnswer'),
          },
          {
            heading: t('helpFaqQuestionsCutoffTimeDrawHeading'),
            detailPart: t('helpFaqQuestionsCutoffTimeDrawAnswer'),
          },
          {
            heading: t('helpFaqQuestionsFeesOnStatementHeading'),
            detailPart: t('helpFaqQuestionsFeesOnStatementAnswer'),
          },
          {
            heading: t('helpFaqQuestionsPayBalanceToZeroHeading'),
            detailPart: t('helpFaqQuestionsPayBalanceToZeroAnswer'),
          },
          {
            heading: t('helpFaqQuestionsPayFullBalanceHeading'),
            detailPart: t('helpFaqQuestionsPayFullBalanceAnswer'),
          },
          {
            heading: t('helpFaqQuestionsSmallestDrawHeading'),
            detailPart: t('helpFaqQuestionsSmallestDrawAnswer'),
          },
          { heading: t('helpFaqQuestionsMaxDrawHeading'), detailPart: t('helpFaqQuestionsMaxDrawAnswer') },
          {
            heading: t('helpFaqQuestionsDrawAfterFundingHeading'),
            detailPart: t('helpFaqQuestionsDrawAfterFundingAnswer'),
          },
          {
            heading: t('helpFaqQuestionsWrittenRequestReinstateHeading'),
            detailPart: t('helpFaqQuestionsWrittenRequestReinstateAnswer'),
          },
          {
            heading: t('helpFaqQuestionsLoanAssumableHeading'),
            detailPart: t('helpFaqQuestionsLoanAssumableAnswer'),
          },
          {
            heading: t('helpFaqQuestionsEscrowTaxesInsuranceHeading'),
            detailPart: t('helpFaqQuestionsEscrowTaxesInsuranceAnswer'),
          },
          {
            heading: t('helpFaqQuestionsStatementNoBalanceHeading'),
            detailPart: t('helpFaqQuestionsStatementNoBalanceAnswer'),
          },
          {
            heading: t('helpFaqQuestionsSubordinateHelocHeading'),
            detailPart: t('helpFaqQuestionsSubordinateHelocAnswer'),
          },
          {
            heading: t('helpFaqQuestionsFfcWhatHeading'),
            detailPart: t('helpFaqQuestionsFfcWhatAnswer'),
          },
          {
            heading: t('helpFaqQuestionsFfcInfoHeading'),
            detailPart: t('helpFaqQuestionsFfcInfoAnswer'),
          },
        ],
      },
      {
        faqType: t('helpFaqCategoriesTaxesInsurance'),
        faqQuestionAnswers: [
          {
            heading: t('propertyTaxesFaqSupplementalHeading'),
            detailPart: t('propertyTaxesFaqSupplementalDescription'),
          },
          {
            heading: t('helpFaqQuestionsPaymentChangeTaxAssessmentHeading'),
            detailPart: t('propertyTaxesFaqAssessmentDescription'),
          },
          {
            heading: t('helpFaqQuestionsTaxBillFromAuthorityHeading'),
            detailPart: t('helpFaqQuestionsTaxBillFromAuthorityAnswer'),
          },
          {
            heading: t('helpFaqQuestionsDelinquentTaxNoticesHeading'),
            detailPart: t('helpFaqQuestionsDelinquentTaxNoticesAnswer'),
          },
          {
            heading: t('helpFaqQuestionsInsuranceBillHeading'),
            detailPart: t('helpFaqQuestionsInsuranceBillAnswer'),
          },
          {
            heading: t('helpFaqQuestionsInsuranceClaimHeading'),
            detailPart: t('helpFaqQuestionsInsuranceClaimAnswer'),
          },
        ],
      },
    ];
  });

  protected readonly filteredCategories = computed<FaqCategory[]>(() => {
    const categories = this.faqCategories();
    const search = this.searchText().toLowerCase().trim();

    if (!search) {
      return categories;
    }

    return categories
      .map((category) => ({
        ...category,
        faqQuestionAnswers: category.faqQuestionAnswers.filter(
          (qa) => qa.heading.toLowerCase().includes(search),
        ),
      }))
      .filter((category) => category.faqQuestionAnswers.length > 0);
  });

  protected readonly visibleQuestionCount = computed(() =>
    this.filteredCategories().reduce((sum, cat) => sum + cat.faqQuestionAnswers.length, 0),
  );

  protected onSearchChange(value: string): void {
    this.searchText.set(value);
  }

  protected trackByCategory(_index: number, category: FaqCategory): string {
    return category.faqType;
  }

  protected trackByQuestion(_index: number, question: FaqQuestionAnswer): string {
    return question.heading;
  }

  protected onShareScreenClick(): Promise<void> {
    return this.shareScreenService.startSession('HelpFaqComponent');
  }

  protected openChat(): void {
    this.servisBotService.open();
  }

  protected openMessageCenter(): void {
    if (this.accountId) {
      this.router.navigate(['/messages', this.accountId]);
    } else {
      this.router.navigate(['/dashboard']);
    }
  }

  protected toSlug(heading: string): string {
    return heading
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  }

  ngAfterViewInit(): void {
    this.route.fragment.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((fragment) => {
      if (fragment) {
        this.expandedSlug.set(fragment);
        setTimeout(() => {
          document.getElementById(fragment)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 400);
      }
    });

    this.elementRef.nativeElement.addEventListener('click', this.faqClickHandler);
  }

  ngOnDestroy(): void {
    this.elementRef.nativeElement.removeEventListener('click', this.faqClickHandler);
  }
}
